import { useState, useEffect, useMemo } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Typography, Button, Checkbox, CircularProgress, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import BasicTabs from '../common/BasicTabs';
import StandardTable from '../tables/StandardTable';
import { useAppDispatch } from '../../app/hooks';
import { selectUser } from '../../features/auth/authSlice';
import { selectProject } from '../../features/project/projectSlice';
import { IDeal, IDealDeposit, IDealRealtor } from '../../types/CreateDealForm';
import {
  customSort,
  numberWithCommas,
  numToCurrency,
  camelToTitle,
  convertAllDates,
  downloadExcel,
  downloadPdf,
  getSalesStatusCodes,
} from '../../utils/Functions';
import { showErrorSnackbar, showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import { IPurchaserInfo } from '../../types/CreateDealForm';
import { Flex, FlexBetween, FlexEnd } from '../../commonStyles';
import { handleModal } from '../../features/modal/modalSlice';
import Dropdown from '../common/formControls/Dropdown';
import { chequeTypes, libertyProjects } from '../../utils/Constants';
import { GlobalModal } from '../../features/modal/Modal';
import OutstandingProfile from './OutstandingProfile';
import { IDocuments } from '../../types/docusign';

// Proof of identity entered on the purchaser (Proof Type / Proof ID Number / Proof Expiry): the type when it's
// entered and current, otherwise 'Expired' or 'Missing'. Shared by the table column and the download.
const proofIdStatus = (purchaser: any) => {
  if (!purchaser.proofType || !purchaser.proofNumber) return 'Missing';
  if (purchaser.proofExpiry && new Date(purchaser.proofExpiry).valueOf() < new Date().valueOf()) return 'Expired';
  return purchaser.proofType;
};

// Liberty projects: the supporting documents still missing from the deal's Miscellaneous uploads - an upload with
// "Email" in its name covers the Email Confirmation, one with "Lawyer" covers the Lawyer Information
const libertyMissingDocuments = (deal: any) => {
  const names = (deal.miscellaneous || []).map((miscellaneous: any) => `${miscellaneous.name || ''}`.toLowerCase());
  const missing: string[] = [];
  if (!names.some((name: string) => name.includes('email'))) missing.push('Email Confirmation');
  if (!names.some((name: string) => name.includes('lawyer'))) missing.push('Lawyer Information');
  return missing;
};

// userType 'sales' (the Sales dashboard) limits every list and download to the logged-in rep's own deals
const NewOutstandings = ({ userType }: { userType?: string }) => {
  const navigate = useNavigate();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const isLiberty = libertyProjects.includes(project._id!);
  const baseVariables = userType === 'sales' ? { project: project._id, salesRep: user._id } : { project: project._id };
  const storeDispatch = useAppDispatch();
  const [value, setValue] = useState<number>(0);
  const [deals, setDeals] = useState<IDeal[]>([]);
  const [allDeals, setAllDeals] = useState<IDeal[]>([]);
  const [selectedDeposit, setSelectedDeposit] = useState<IDealDeposit | null>(null);
  const [selectedDeals, setSelectedDeals] = useState<IDeal[]>([]);
  const [selectedRealtor, setSelectedRealtor] = useState<IDealRealtor | null>(null);
  const [type, setType] = useState<string>('');

  const [updateDeal] = useMutation(UPDATEDEAL, {
    onCompleted: (data) => {
      setDeals(deals.filter((deal: IDeal) => deal._id !== data.dealUpdateById.record._id));
      setType('');
      storeDispatch(handleModal(false));
      setSelectedDeals([]);
      setSelectedDeposit(null);
      storeDispatch(showSuccessSnackbar('Updated!'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [getOutstandings, { loading }] = useLazyQuery(DEALS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: async (data) => {
      let sortedRows = await customSort(data.getOutstandings, 'unit', 'suite', true);
      setDeals(sortedRows);
      setAllDeals(sortedRows);
    },
    onError: (error) => {
      storeDispatch(showErrorSnackbar(error.toString()));
    },
  });

  const [sendOutstandings, { loading: outstandingsLoading }] = useMutation(SENDOUTSTANDINGS, {
    onCompleted: (data) => {
      setType('');
      setSelectedDeals([]);
      setSelectedDeposit(null);
      storeDispatch(handleModal(false));
      storeDispatch(showSuccessSnackbar('Outstanding email(s) have been sent'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateDealDeposit] = useMutation(UPDATEDEPOSIT, {
    onCompleted: (data) => {
      let updatedActiveDeals = deals.map((deal: any) => {
        if (deal._id === selectedDeals[0]._id) {
          let deposit = data.updateDeposits;
          let pendingDeposits = selectedDeals[0].pendingDeposits.map((deposit: any) => {
            if (deposit._id === selectedDeposit?._id) {
              return selectedDeposit;
            } else return deposit;
          });
          return {
            ...deal,
            deposit: deposit,
            pendingDeposits: pendingDeposits,
          };
        } else return deal;
      });

      setDeals(updatedActiveDeals);
      setType('');
      storeDispatch(handleModal(false));
      setSelectedDeals([]);
      setSelectedDeposit(null);
      storeDispatch(showSuccessSnackbar('Deposit has been updated!'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const getStatusDate = (deal: IDeal) => {
    let date;
    switch (deal.unit.status) {
      case 'F':
        date = deal.firmDate;
        break;
      case 'S':
        date = deal.signDate;
        break;
      case 'C':
        date = deal.executeDate;
        break;
      default:
        // Custom sales-category statuses (e.g. brokerage-specific sale codes) are treated as
        // firmed, same as F - Conditional is excluded since it already has its own case above.
        date = getSalesStatusCodes(project).filter((code: string) => code !== 'C').includes(deal.unit.status)
          ? deal.firmDate
          : deal.createdAt;
        break;
    }
    return convertAllDates(date, 'PP');
  };

  useEffect(() => {
    if (!value) {
      getOutstandings({ variables: { ...baseVariables, type: 'incomplete' } });
    } else if (value === 1) {
      getOutstandings({ variables: { ...baseVariables, type: 'escalated' } });
    } else if (value === 2) {
      getOutstandings({ variables: { ...baseVariables, type: 'doNotTouch' } });
    } else if (value === 3) {
      getOutstandings({ variables: { ...baseVariables, type: 'default' } });
    } else if (value === 4) {
      getOutstandings({ variables: { ...baseVariables, type: 'extension' } });
    } else if (value === 5) {
      getOutstandings({ variables: { ...baseVariables } });
    }
  }, [value]);

  const viewModal = (type: string, deal: any, deposit: any = null, realtor: any = null) => {
    if (deposit) {
      setSelectedDeposit(deposit);
    }
    if (deal) {
      setSelectedDeals([deal]);
      setSelectedRealtor(null);
    }
    if (type === 'information' && realtor) {
      setSelectedDeals(
        deals.filter((deal: IDeal) => deal.realtor.some((selectedRealtor: IDealRealtor) => realtor.fullName === selectedRealtor?.fullName))
      );
      setSelectedRealtor(realtor);
    }
    setType(type);
    storeDispatch(handleModal(true));
  };

  const handleDropdownInput = (e: any) => {
    setSelectedDeposit({
      ...selectedDeposit,
      chequeType: e.target.value,
    });
  };

  const saveDeposit = () => {
    if (selectedDeposit && selectedDeals.length === 1) {
      let record: IDealDeposit = {
        chequeType: selectedDeposit.chequeType,
      };

      if (selectedDeposit.chequeType === 'cleared') {
        record.clearedDate = new Date();
      }

      let deposits = selectedDeals[0].deposit;
      if (selectedDeals[0].pendingDeposits && selectedDeals[0].pendingDeposits.length) {
        deposits = selectedDeals[0].pendingDeposits;
      }

      let newDeposits = deposits.map((deposit: IDealDeposit) => {
        if (deposit._id === selectedDeposit._id) {
          return {
            ...deposit,
            chequeType: selectedDeposit.chequeType,
            clearedDate: selectedDeposit.chequeType === 'cleared' ? new Date() : null,
          };
        } else
          return {
            ...deposit,
          };
      });

      updateDealDeposit({
        variables: {
          depositArray:
            selectedDeals[0].pendingDeposits && selectedDeals[0].pendingDeposits.length ? selectedDeals[0].deposit : newDeposits,
          additionalDepositArray: [],
          pendingDepositArray: selectedDeals[0].pendingDeposits && selectedDeals[0].pendingDeposits.length ? newDeposits : [],
          unitId: selectedDeals[0].unit._id,
          dealId: selectedDeals[0]._id,
        },
      });
    }
  };

  const handleSelectedDeal = (deal: IDeal) => {
    if (selectedDeals.some((selectedDeal: IDeal) => deal._id === selectedDeal._id)) {
      let deals = selectedDeals.filter((selectedDeal: IDeal) => deal._id !== selectedDeal._id);
      setSelectedDeals(deals);
    } else {
      setSelectedDeals([...selectedDeals, deal]);
    }
  };

  const modalView = () => {
    switch (type) {
      case 'information':
        return <OutstandingProfile deals={selectedDeals} realtor={selectedRealtor} updateDeal={updateDeal} />;
      case 'depositChange':
        return (
          <Box>
            <Typography variant="h2" component="div" gutterBottom>
              {selectedDeals[0].unit.suite} - {selectedDeposit?.name}
              <Box sx={{ my: 2 }}>
                <Dropdown
                  id={'dropdown'}
                  title={'Type'}
                  menuList={chequeTypes.map((chequeType: any) => chequeType.value.toLowerCase())}
                  name={'chequeInfo'}
                  handleSelect={(e: any) => handleDropdownInput(e)}
                  value={selectedDeposit?.chequeType}
                />
              </Box>
              <Button color="success" variant="contained" onClick={() => saveDeposit()}>
                Update
              </Button>
            </Typography>
          </Box>
        );
      case 'sendOutstandings':
        return (
          <Box>
            <Typography variant="h2" component="div" gutterBottom>
              Send Outstanding Notices
            </Typography>
            <p>Please select the suites below that you would like to send outstanding notices to.</p>
            {selectedDeals.length ? (
              <p>
                Selected: <strong style={{ color: 'green' }}>{selectedDeals.length}</strong>
              </p>
            ) : null}
            <StandardTable columns={outstandingColumns} data={deals} />
            <FlexEnd sx={{ mt: 2 }}>
              <Button
                sx={{ mr: 2 }}
                onClick={() => {
                  if (!selectedDeals.length) return storeDispatch(showErrorSnackbar('Please select a deal to preview'));
                  sendOutstandings({ variables: { deals: selectedDeals.map((deal) => deal._id), preview: true } });
                }}
                color="primary"
                variant="contained"
              >
                {outstandingsLoading ? <CircularProgress sx={{ color: '#fff' }} size={20} /> : 'Send Preview To Your Email'}
              </Button>
              <Button
                color="success"
                variant="contained"
                onClick={() => {
                  if (!selectedDeals.length) return storeDispatch(showErrorSnackbar('Please select at least one deal'));
                  sendOutstandings({ variables: { deals: selectedDeals.map((deal) => deal._id), preview: false } });
                }}
              >
                Send
              </Button>
            </FlexEnd>
          </Box>
        );
    }
  };

  const columns = useMemo(() => {
    const viewMortgage = (url: string) => {
      const element = document.createElement('a');
      element.href = url;
      element.target = '_blank';
      element.click();
    };
    return [
      {
        Header: 'Suite',
        accessor: (rowData: any) => {
          return (
            <Box>
              <Link
                style={{ textDecoration: 'none', color: 'inherit' }}
                rel="noopener noreferrer"
                target="_blank"
                to={`/${rowData.project._id}/dashboard/${rowData.unit._id}`}
              >
                <strong>
                  {rowData.unit.suite} - {rowData.unit.status}
                </strong>
              </Link>
              <Box>
                <strong>{getStatusDate(rowData)}</strong>
              </Box>
            </Box>
          );
        },
      },
      {
        Header: 'Purchaser',
        accessor: (rowData: any) => {
          return rowData.purchasers.map((purchaser: IPurchaserInfo, index: number) => {
            return (
              <Box key={index} sx={{ cursor: 'pointer' }} onClick={() => viewModal('information', rowData)}>
                <strong>{purchaser.fullName}</strong>
              </Box>
            );
          });
        },
      },
      {
        Header: 'Proof ID',
        accessor: (rowData: any) => {
          return rowData.purchasers.map((purchaser: any, index: number) => {
            const status = proofIdStatus(purchaser);
            const hasProof = status !== 'Missing';
            return (
              <Tooltip
                key={index}
                title={
                  hasProof
                    ? `${purchaser.proofType}${purchaser.proofExpiry ? ` - expires ${convertAllDates(purchaser.proofExpiry, 'PP')}` : ''}`
                    : 'No proof ID entered'
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '12px' }}>
                  <span>{purchaser.fullName}:</span>
                  <strong style={{ color: status === 'Missing' || status === 'Expired' ? 'red' : 'green' }}>{status}</strong>
                </Box>
              </Tooltip>
            );
          });
        },
      },
      {
        Header: 'Mortgage',
        accessor: (rowData: any) => {
          if (
            (rowData.mortgage.amount && rowData.mortgage.getUrl && rowData.mortgage.lender && !rowData.mortgage.revisions.length) ||
            rowData.mortgage.revisions.includes('No MPA Required')
          ) {
            return <CheckIcon color="success" />;
          } else {
            let currentDeposit = rowData.deposit;
            if (rowData.pendingDeposits.length) {
              currentDeposit = rowData.pendingDeposits;
            }
            let totalDeposits = currentDeposit.reduce((a: any, b: any) => {
              return a + b.amount;
            }, 0);
            return (
              <Box>
                {!rowData.mortgage.getUrl ? (
                  <Box sx={{ color: 'red', fontSize: '10px' }}>
                    <strong>Missing</strong>
                  </Box>
                ) : null}
                <Box
                  sx={{ fontWeight: rowData.mortgage.getUrl ? 700 : 500, cursor: rowData.mortgage.getUrl ? 'pointer' : 'auto' }}
                  onClick={() => {
                    if (rowData.mortgage.getUrl) {
                      viewMortgage(rowData.mortgage.getUrl);
                    }
                  }}
                >{`$${numberWithCommas(rowData.totalPrice - totalDeposits)}`}</Box>
                <Box>
                  {rowData.mortgage.revisions.map((revision: string, index: number) => {
                    return (
                      <Box key={index} sx={{ fontSize: '10px' }}>
                        <strong>{revision}</strong>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          }
        },
      },
      {
        Header: 'Deposits',
        accessor: (rowData: any) => {
          let currentDeposit = rowData.deposit;
          if (rowData.pendingDeposits.length) {
            currentDeposit = rowData.pendingDeposits;
          }
          return (
            <Box>
              {rowData.pendingDeposits.length ? (
                <Box>
                  <strong>PENDING DEPOSITS</strong>
                </Box>
              ) : null}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {currentDeposit.map((deposit: IDealDeposit, index: number) => {
                  let outstandingDeposit =
                    deposit.name !== 'Occupancy' &&
                    deposit.name !== 'Occupancy Deposit' &&
                    (deposit.chequeType === '' || deposit.chequeType === 'nsf' || deposit.chequeType === 'replacement') &&
                    new Date().valueOf() > new Date(deposit.dueDate!).valueOf()
                      ? true
                      : false;
                  return (
                    <Box
                      key={index}
                      sx={{ fontSize: '10px', cursor: 'pointer' }}
                      onClick={() => viewModal('depositChange', rowData, deposit)}
                    >
                      <strong>{deposit.name}</strong> - {numToCurrency.format(deposit.amount!)} -{' '}
                      <Box sx={{ display: 'inline-block', color: outstandingDeposit ? 'red' : '#000' }}>
                        {convertAllDates(deposit.dueDate, 'PP')}
                      </Box>{' '}
                      - {deposit.chequeType ? camelToTitle(deposit.chequeType!) : 'Not Received'}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        },
      },
      {
        Header: 'Documents',
        accessor: (rowData: any) => {
          return rowData.documents.map((document: any) => {
            let outstandingDocument = document.status !== 'Completed' ? true : false;
            return (
              <Box sx={{ fontSize: '10px' }}>
                {document.name} - <Box sx={{ display: 'inline-block', color: outstandingDocument ? 'red' : '#000' }}>{document.status}</Box>
              </Box>
            );
          });
        },
      },
      ...(isLiberty
        ? [
            {
              Header: 'Liberty Additional Documents',
              accessor: (rowData: any) =>
                libertyMissingDocuments(rowData).map((name: string) => (
                  <Box key={name} sx={{ fontSize: '10px', color: 'red' }}>
                    <strong>{name}</strong>
                  </Box>
                )),
            },
          ]
        : []),
      {
        Header: 'Realtor Name',
        accessor: (rowData: any) => {
          if (rowData.realtor.length) {
            return rowData.realtor[0].fullName;
          } else {
            if (rowData.tags.includes('Friends and Family')) {
              return 'Friends and Family';
            } else return 'Public';
          }
        },
        Cell: (props: any) => {
          if (props.value === 'Public' || props.value === 'Friends and Family') {
            return props.value;
          } else {
            return (
              <Box sx={{ cursor: 'pointer' }} onClick={() => viewModal('information', null, null, props.row.original.realtor[0])}>
                <strong>{props.value}</strong>
              </Box>
            );
          }
        },
      },
      {
        Header: 'Realtor Contact',
        accessor: (rowData: any) => {
          if (rowData.realtor.length) {
            return (
              <Box>
                <Box>{rowData.realtor[0].email}</Box>
                <Box>{rowData.realtor[0].directPhone}</Box>
              </Box>
            );
          } else return '';
        },
      },
    ];
  }, [deals]);

  const outstandingColumns = useMemo(() => {
    return [
      {
        id: 'selection',
        Header: (
          <Checkbox
            sx={{ color: '#fff' }}
            onClick={() => {
              if (selectedDeals.length === deals.length) {
                setSelectedDeals([]);
              } else {
                setSelectedDeals(deals);
              }
            }}
            checked={selectedDeals.length > 0}
          />
        ),
        accessor: (rowData: IDeal) => {
          return (
            <Box>
              <Checkbox
                onChange={(e) => handleSelectedDeal(rowData)}
                checked={selectedDeals.some((deal: IDeal) => deal._id === rowData._id)}
              />
            </Box>
          );
        },
      },
      ...columns,
    ];
  }, [selectedDeals]);

  const handleGlobalFilter = (stringValue: string) => {
    if (stringValue) {
      let all = [...allDeals];
      let filteredDeals = all.filter(
        (deal: IDeal) =>
          deal.documents.some((document: IDocuments) => document.name.includes(stringValue)) ||
          deal.purchasers.some((purchaser: IPurchaserInfo) => purchaser.firstName.includes(stringValue)) ||
          deal.realtor.some((realtor: IDealRealtor) => realtor.firstName?.includes(stringValue) || realtor.lastName?.includes(stringValue))
      );
      setDeals(filteredDeals);
    } else {
      if (!value) {
        getOutstandings({ variables: { ...baseVariables, type: 'incomplete' } });
      } else if (value === 1) {
        getOutstandings({ variables: { ...baseVariables, type: 'escalated' } });
      } else if (value === 2) {
        getOutstandings({ variables: { ...baseVariables, type: 'doNotTouch' } });
      } else if (value === 3) {
        getOutstandings({ variables: { ...baseVariables } });
      }
    }
  };

  const download = async (type: string, data: any) => {
    let outstandings: any = [];
    if (!value) {
      outstandings = await getOutstandings({ variables: { ...baseVariables, type: 'incomplete', download: true } });
    } else if (value === 1) {
      outstandings = await getOutstandings({ variables: { ...baseVariables, type: 'escalated', download: true } });
    } else if (value === 2) {
      outstandings = await getOutstandings({ variables: { ...baseVariables, type: 'doNotTouch', download: true } });
    } else if (value === 3) {
      outstandings = await getOutstandings({ variables: { ...baseVariables, type: 'default', download: true } });
    } else if (value === 4) {
      outstandings = await getOutstandings({ variables: { ...baseVariables, type: 'extension', download: true } });
    } else if (value === 5) {
      outstandings = await getOutstandings({ variables: { ...baseVariables, download: true } });
    }

    let headers = [
      {
        label: 'Suite',
        id: 'suite',
      },
      {
        label: 'Status',
        id: 'status',
      },
      {
        label: 'Status Date',
        id: 'statusDate',
      },
      {
        label: 'Purchasers',
        id: 'purchasers',
      },
      {
        label: 'Proof ID',
        id: 'proofId',
      },
      {
        label: 'Mortgage',
        id: 'mortgage',
      },
      {
        label: 'Deposits',
        id: 'deposits',
      },
      {
        label: 'Documents',
        id: 'documents',
      },
      ...(isLiberty
        ? [
            {
              label: 'Liberty Additional Documents',
              id: 'libertyDocuments',
            },
          ]
        : []),
      {
        label: 'Realtor',
        id: 'realtor',
      },
      {
        label: 'Realtor Contact',
        id: 'realtorContact',
      },
      {
        label: 'Notes',
        id: 'notes',
      },
    ];

    let exWidths = {
      suite: 15,
      status: 15,
      statusDate: 15,
      purchasers: 15,
      proofId: 15,
      mortgage: 15,
      deposits: 15,
      documents: 15,
      libertyDocuments: 15,
      realtor: 15,
      realtorContact: 15,
      notes: 15
    };

    let fixedWidths = {
      suite: 200,
      status: 200,
      statusDate: 200,
      purchasers: 200,
      proofId: 200,
      mortgage: 200,
      deposits: 200,
      documents: 200,
      libertyDocuments: 200,
      realtor: 200,
      realtorContact: 200,
      notes: 200
    };

    let sortedRows = await customSort(outstandings.data.getOutstandings, 'unit', 'suite', true);

    let allData = sortedRows.map((deal: IDeal) => {
      let currentDeposit = deal.deposit;
      if (deal.pendingDeposits.length) {
        currentDeposit = deal.pendingDeposits;
      }
      let totalDeposits = currentDeposit.reduce((a: any, b: any) => {
        return a + b.amount;
      }, 0);
      return {
        suite: deal.unit.suite,
        status: deal.unit.status,
        statusDate: getStatusDate(deal),
        purchasers: deal.purchasers.map((purchaser: IPurchaserInfo) => purchaser.fullName).join(', '),
        proofId: deal.purchasers.map((purchaser: any) => `${purchaser.fullName}: ${proofIdStatus(purchaser)}\r\n`).join(''),
        mortgage:
          (deal.mortgage.amount && deal.mortgage.getUrl && deal.mortgage.lender && !deal.mortgage.revisions.length) ||
          deal.mortgage.revisions.includes('No MPA Required')
            ? 'Received'
            : `${!deal.mortgage.getUrl ? 'Missing\r\n' : ''}$${numberWithCommas(deal.totalPrice! - totalDeposits)}${deal.mortgage.revisions
                .map((revision: string) => `\r\n${revision}`)
                .join('')}`,
        deposits: deal.deposit
          .map((deposit: IDealDeposit) => {
            return `${deposit.name} - ${numToCurrency.format(deposit.amount!)} - ${convertAllDates(deposit.dueDate, 'PP')} - ${
              deposit.chequeType ? camelToTitle(deposit.chequeType) : 'Not Received'
            }\r\n`;
          })
          .join(''),
        documents: deal.documents
          .map((document: IDocuments) => {
            return `${document.name} - ${document.status}\r\n`;
          })
          .join(''),
        libertyDocuments: libertyMissingDocuments(deal)
          .map((name: string) => `${name}\r\n`)
          .join(''),
        realtor: deal.realtor.length ? deal.realtor[0].fullName : deal.tags.includes('Friends and Family') ? 'Friends and Family' : 'Public',
        realtorContact: deal.realtor.length ? `${deal.realtor[0].email}, ${deal.realtor[0].directPhone}` : '',
        notes: deal.comments.length ? deal.comments[deal.comments.length - 1].comment : ''
      };
    });

    let sheetTitle = `${project.name} - Outstandings`;

    if (type === 'excel') {
      downloadExcel([allData], [headers], [], [[exWidths]], [sheetTitle], sheetTitle);
    } else {
      downloadPdf([allData], [headers], [], [fixedWidths], [sheetTitle], sheetTitle);
    }
  };

  const tabs = [
    {
      label: 'Incomplete Files',
      component: (
        <StandardTable
          data={deals}
          columns={columns}
          loading={loading}
          count={deals.length}
          handleGlobalFilterValue={handleGlobalFilter}
          download={download}
        />
      ),
    },
    {
      label: 'Escalated',
      component: (
        <StandardTable
          data={deals}
          columns={columns}
          loading={loading}
          count={deals.length}
          handleGlobalFilterValue={handleGlobalFilter}
          download={download}
        />
      ),
    },
    {
      label: 'Do Not Touch',
      component: (
        <StandardTable
          data={deals}
          columns={columns}
          loading={loading}
          count={deals.length}
          handleGlobalFilterValue={handleGlobalFilter}
          download={download}
        />
      ),
    },
    {
      label: 'Default',
      component: (
        <StandardTable
          data={deals}
          columns={columns}
          loading={loading}
          count={deals.length}
          handleGlobalFilterValue={handleGlobalFilter}
          download={download}
        />
      ),
    },
    {
      label: 'Extension',
      component: (
        <StandardTable
          data={deals}
          columns={columns}
          loading={loading}
          count={deals.length}
          handleGlobalFilterValue={handleGlobalFilter}
          download={download}
        />
      ),
    },
    {
      label: 'All',
      component: (
        <StandardTable
          data={deals}
          columns={columns}
          loading={loading}
          count={deals.length}
          handleGlobalFilterValue={handleGlobalFilter}
          download={download}
        />
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <GlobalModal loading={outstandingsLoading}>{modalView()}</GlobalModal>
      <FlexBetween>
        <Typography variant="h2" sx={{ alignSelf: 'center' }}>
          <strong>{userType === 'sales' ? `${user.firstName}'s ` : ''}Outstandings</strong>
        </Typography>
        <Flex>
          {deals.length && value !== 2 ? (
            <Button
              variant="contained"
              onClick={() => {
                setSelectedDeals([]);
                viewModal('sendOutstandings', null);
              }}
              sx={{ mr: 1 }}
            >
              Send Outstandings
            </Button>
          ) : null}
          <Button variant="contained" onClick={() => navigate(`/${project._id}/dashboard/outstandings-old`)}>
            Old View
          </Button>
        </Flex>
      </FlexBetween>
      <Box sx={{ my: 2 }}>
        <BasicTabs tabs={tabs} sub={true} value={value} setValue={setValue} color={'#fff'} />
      </Box>
    </Box>
  );
};

const DEALS = gql`
  query getOutstandings($project: MongoID!, $salesRep: MongoID, $type: String, $download: Boolean) {
    getOutstandings(project: $project, salesRep: $salesRep, type: $type, download: $download) {
      _id
      project {
        _id
      }
      unit {
        _id
        status
        suite
        history {
          type
          description
          priceFrom
          priceTo
          timestamp
          user {
            _id
            fullName
          }
        }
      }
      purchasers {
        firstName
        fullName
        email
        primaryPhone
        proofType
        proofNumber
        proofExpiry
      }
      realtor {
        email
        fullName
        directPhone
      }
      deposit {
        _id
        name
        amount
        dueDate
        accountNumber
        chequeDate
        chequeType
        chequeAmount
        chequeNumber
      }
      pendingDeposits {
        _id
        name
        amount
        dueDate
        accountNumber
        chequeDate
        chequeType
        chequeAmount
        chequeNumber
      }
      options {
        amount
      }
      tags
      mortgage {
        revisions
        lender
        amount
        getUrl
      }
      additionalDeposits {
        name
        chequeType
      }
      documents {
        _id
        name
        status
      }
      miscellaneous {
        _id
        name
      }
      comments {
        _id
        type
        comment
        createdAt
        user {
          fullName
        }
      }
      createdAt
      signDate
      executeDate
      firmDate
      basePrice
      totalPrice
      type
    }
  }
`;

const UPDATEDEPOSIT = gql`
  mutation updateDeposits(
    $depositArray: [updateCurrentDepositInput!]
    $additionalDepositArray: [updateDepositInput!]
    $pendingDepositArray: [updateDepositInput!]
    $unitId: MongoID!
    $dealId: MongoID!
  ) {
    updateDeposits(
      depositArray: $depositArray
      additionalDepositArray: $additionalDepositArray
      pendingDepositArray: $pendingDepositArray
      unitId: $unitId
      dealId: $dealId
    ) {
      _id
      name
      amount
      dueDate
      accountNumber
      chequeNumber
      chequeDate
      chequeType
      chequeAmount
    }
  }
`;

const SENDOUTSTANDINGS = gql`
  mutation sendOutstandings($deals: [MongoID!], $preview: Boolean!) {
    sendOutstandings(deals: $deals, preview: $preview)
  }
`;

const UPDATEDEAL = gql`
  mutation dealUpdateById($_id: MongoID!, $record: UpdateByIdDealInput!) {
    dealUpdateById(_id: $_id, record: $record) {
      record {
        _id
        project {
          _id
        }
        unit {
          _id
          status
          suite
          history {
            type
            description
            priceFrom
            priceTo
            timestamp
            user {
              _id
              fullName
            }
          }
        }
        purchasers {
          firstName
          fullName
          email
          primaryPhone
        }
        realtor {
          email
          fullName
          directPhone
        }
        deposit {
          _id
          name
          amount
          dueDate
          accountNumber
          chequeDate
          chequeType
          chequeAmount
          chequeNumber
        }
        pendingDeposits {
          _id
          name
          amount
          dueDate
          accountNumber
          chequeDate
          chequeType
          chequeAmount
          chequeNumber
        }
        options {
          amount
        }
        tags
        mortgage {
          revisions
          lender
          amount
          getUrl
        }
        additionalDeposits {
          name
          chequeType
        }
        documents {
          _id
          name
          status
        }
        comments {
          _id
          type
          pinned
          comment
          updatedAt
          createdAt
          user {
            fullName
          }
        }
        createdAt
        signDate
        executeDate
        firmDate
        basePrice
        totalPrice
        type
      }
    }
  }
`;

export default NewOutstandings;
